import { useCallback, useEffect, useMemo, useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import { shopModeApi, type FloorBoxSummary } from "../../api/shopMode";
import type { Zone } from "../../api/types";
import { useT } from "../../i18n/LanguageProvider";
import { matchesBoxSearch } from "../../utils/boxSearch";
import { boxContentsSummary } from "../../utils/boxSummary";
import { ShopFloorBoxCard } from "./ShopFloorBoxCard";
import { ShopFloorIcon } from "./ShopFloorIcon";
import { ShopFloorItemCard } from "./ShopFloorItemCard";
import { ShopFloorNewBox } from "./ShopFloorNewBox";

/**
 * G-84 (OD-51) — the Boxes tab: every active box in this shop, searchable
 * by code, name or where it is kept; tap one to see what is inside and print
 * its barcode. "Create a new box" lives HERE AND NOWHERE ELSE: it sat in the
 * Find-an-item search row too until OD-52 (2026-09-17) took it out, because
 * that row is for finding an item. This is the only door that issues one.
 *
 * The search field is deliberately not autofocused: on the shop's tablet the
 * soft keyboard covers half the screen (G-75).
 */
export function ShopFloorBoxList({ shelfZones }: { shelfZones: Zone[] }) {
  const { t } = useT();
  const [boxes, setBoxes] = useState<FloorBoxSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [openBoxId, setOpenBoxId] = useState<string | null>(null);
  const [openItemId, setOpenItemId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setBoxes(await shopModeApi.floorListBoxes());
    } catch (err: unknown) {
      // `translate`, not the hook's `t`: this callback guards a fetch, and a
      // `t` in its dependency list would refetch the boxes on a language switch.
      setError(errorText(err, "box.couldNotLoad"));
      setBoxes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const shown = useMemo(() => boxes.filter((b) => matchesBoxSearch(b, query)), [boxes, query]);

  return (
    <div className="shop-floor-stock-flow sf-finder sf-box-list">
      <div className="sf-finder-bar">
        <label htmlFor="sf-box-search" className="visually-hidden">
          {t("box.searchLabel")}
        </label>
        <div className="inventory-search-field sf-finder-field">
          <ShopFloorIcon name="search" size={22} />
          <input
            id="sf-box-search"
            type="search"
            className="inventory-search-input"
            placeholder={t("box.searchPlaceholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
          />
        </div>
        <button
          type="button"
          className="button sf-finder-scan-btn"
          onClick={() => setCreating(true)}
        >
          {t("box.createNew")}
        </button>
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      {loading ? (
        <div className="empty-state">{t("box.loading")}</div>
      ) : boxes.length === 0 ? (
        <div className="empty-state sf-box-empty">
          <p className="sf-box-empty-title">{t("box.noneYet")}</p>
          <p className="muted">{t("box.noneYetHint")}</p>
        </div>
      ) : shown.length === 0 ? (
        <div className="empty-state">{t("box.noMatch", { query: query.trim() })}</div>
      ) : (
        <ul className="sf-finder-results sf-box-rows">
          {shown.map((b) => (
            <li key={b.id}>
              <button type="button" className="sf-finder-result" onClick={() => setOpenBoxId(b.id)}>
                <span className="sf-box-row-icon" aria-hidden="true">
                  <ShopFloorIcon name="box" size={28} />
                </span>
                <span className="sf-finder-result-main">
                  <span className="sf-finder-result-name">
                    {b.code}
                    {b.name ? ` · ${b.name}` : ""}
                  </span>
                  <span className="sf-finder-result-where">
                    {boxContentsSummary(b.itemCount, b.totalUnits)}
                    {b.keptInZone
                      ? ` · ${t("box.keptInZoneLower", { zone: b.keptInZone.name })}`
                      : ""}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {creating ? (
        <ShopFloorNewBox
          shelfZones={shelfZones}
          onClose={() => {
            setCreating(false);
          }}
          onCreated={() => void load()}
        />
      ) : null}

      {openBoxId ? (
        <ShopFloorBoxCard
          boxId={openBoxId}
          shelfZones={shelfZones}
          onClose={() => setOpenBoxId(null)}
          onChanged={() => void load()}
          onOpenItem={(itemId) => setOpenItemId(itemId)}
        />
      ) : null}

      {openItemId ? (
        <ShopFloorItemCard
          itemId={openItemId}
          onClose={() => setOpenItemId(null)}
          onChanged={() => void load()}
        />
      ) : null}
    </div>
  );
}
